/** Percusión corporal — sonidos e imágenes en /junior-assets/ */

import { bubbleLaneX, type LaneLayout } from './percusionPerspective';
import type { RhythmFigureId } from './rhythm/rhythmTokens';

export {
  AR_LANE_TOP_Y,
  buildLaneLayouts,
  bubbleLaneX,
  vanishPointX,
  vanishPointY,
  type LaneLayout,
  type PercusionViewport,
} from './percusionPerspective';

export interface PercusionItem {
  id: string;
  label: string;
  emoji: string;
  /** Acento pastel (iconos, etiquetas). */
  color: string;
  /** Fondo mate del botón / ficha. */
  bg: string;
  /** Tinte del carril (cristal esmerilado). */
  laneTint: string;
  figureId: RhythmFigureId;
  soundPath: string;
  imagePath: string;
}

export const PERCUSION_CORPORAL: PercusionItem[] = [
  {
    id: 'palmas',
    label: 'Palmas',
    emoji: '👏',
    color: '#2563EB',
    bg: '#D1E5FF',
    laneTint: 'rgba(209, 229, 255, 0.35)',
    figureId: 'negra',
    soundPath: '/junior-assets/sounds/palmas.mp3',
    imagePath: '/junior-assets/images/palmas.png',
  },
  {
    id: 'patada',
    label: 'Patada',
    emoji: '🦶',
    color: '#EA580C',
    bg: '#FFE4C4',
    laneTint: 'rgba(255, 228, 196, 0.35)',
    figureId: 'blanca',
    soundPath: '/junior-assets/sounds/patada.mp3',
    imagePath: '/junior-assets/images/patada.png',
  },
  {
    id: 'chasquido',
    label: 'Chasquido',
    emoji: '🫰',
    color: '#059669',
    bg: '#A7F3D0',
    laneTint: 'rgba(167, 243, 208, 0.35)',
    figureId: 'corchea_doble',
    soundPath: '/junior-assets/sounds/chasquido.mp3',
    imagePath: '/junior-assets/images/chasquido.png',
  },
  {
    id: 'pecho',
    label: 'Pecho',
    emoji: '💓',
    color: '#DC2626',
    bg: '#FECACA',
    laneTint: 'rgba(254, 202, 202, 0.35)',
    figureId: 'cuartina',
    soundPath: '/junior-assets/sounds/pecho.mp3',
    imagePath: '/junior-assets/images/pecho.png',
  },
];

export function getPercusionById(id: string): PercusionItem | undefined {
  return PERCUSION_CORPORAL.find((p) => p.id === id);
}

export function getPercusionIds(): string[] {
  return PERCUSION_CORPORAL.map((p) => p.id);
}

/** Altura reservada para los círculos de percusión. */
export const PERCUSION_ZONE_PX = 118;

export interface PercusionBucketRect {
  id: string;
  lane: number;
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  r: number;
  /** Posición absoluta dentro del playfield unificado */
  screenLeft: number;
  screenTop: number;
  /** Y donde terminan los carriles (justo encima del círculo) */
  horizonY: number;
}

/**
 * Layout unificado: un solo contenedor de `playfieldHeight` px.
 * Los carriles terminan en `horizonY`; los círculos viven debajo, pegados al borde inferior.
 */
export function buildPercusionBucketRects(
  width: number,
  playfieldHeight: number,
  zoneHeight = PERCUSION_ZONE_PX
): PercusionBucketRect[] {
  const count = PERCUSION_CORPORAL.length;
  const edgePad = Math.max(22, width * 0.05);
  const usable = width - edgePad * 2;
  const spacing = usable / count;
  const r = Math.min(52, spacing * 0.46);

  const bottomPad = 14;
  const cy = playfieldHeight - bottomPad - r;
  const horizonY = cy - r * 0.55;
  const hitTop = cy - r;
  const hitH = r * 2 + bottomPad;

  return PERCUSION_CORPORAL.map((item, i) => {
    const cx = edgePad + spacing * (i + 0.5);
    return {
      id: item.id,
      lane: i,
      x: cx - r,
      y: hitTop,
      w: r * 2,
      h: hitH,
      cx,
      cy,
      r,
      screenLeft: cx - r,
      screenTop: cy - r,
      horizonY,
    };
  });
}

export function laneXAtY(
  lane: number,
  y: number,
  layouts: LaneLayout[]
): number {
  return bubbleLaneX(lane, y, layouts);
}
